<template>
  <div class="p-6">
    <div v-if="basePractices.length > 0">
      <div class="mb-4">
        <h3 class="text-lg font-semibold mb-2 flex items-center gap-2">
          <i class="pi pi-sitemap" style="color: var(--primary-gold);"></i>
          <span style="color: var(--text-primary);">Base Practices</span>
        </h3>
        <p class="text-sm" style="color: var(--text-secondary);">
          The {{ basePractices.length }} base practices of this process domain, with the work products
          each produces, the tooling that supports it, and the SOPs that realize it.
        </p>
      </div>

      <Accordion :activeIndex="0">
        <AccordionTab v-for="bp in basePractices" :key="bp.practice_id">
          <template #header>
            <span class="flex items-center gap-3">
              <span class="font-mono font-semibold" style="color: var(--primary-gold);">{{ bp.practice_id }}</span>
              <span style="color: var(--text-primary);">{{ bp.name }}</span>
            </span>
          </template>

          <div class="space-y-4">
            <p class="text-sm" style="color: var(--text-secondary);">{{ bp.description }}</p>

            <div v-if="sopsFor(bp.practice_id).length">
              <h4 class="text-xs font-semibold uppercase tracking-wide mb-2" style="color: var(--text-tertiary);">Realized by</h4>
              <div class="grid gap-2 md:grid-cols-2">
                <div v-for="sop in sopsFor(bp.practice_id)" :key="sop.sop_id"
                     class="p-3 rounded border cursor-pointer transition-all hover:shadow-sm"
                     style="background: var(--background-primary); border-color: var(--border-light);"
                     @click="openSop(sop.sop_id)">
                  <div class="flex items-start gap-2">
                    <i class="pi pi-book mt-0.5" style="color: var(--primary-gold);"></i>
                    <div class="flex-1 min-w-0">
                      <div class="font-mono text-xs font-semibold" style="color: var(--text-primary);">{{ sop.sop_id }}</div>
                      <div class="text-sm" style="color: var(--text-secondary);">{{ sop.name }}</div>
                      <div v-if="sop.duration || sop.process_output" class="text-xs mt-1" style="color: var(--text-tertiary);">
                        <span v-if="sop.duration">{{ humanize(sop.duration) }}</span>
                        <span v-if="sop.duration && sop.process_output"> · </span>
                        <span v-if="sop.process_output">→ {{ sop.process_output }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="grid gap-4 md:grid-cols-2">
              <div v-if="bp.work_products?.length">
                <h4 class="text-xs font-semibold uppercase tracking-wide mb-2" style="color: var(--text-tertiary);">Work products</h4>
                <ul class="space-y-1 text-sm" style="color: var(--text-secondary);">
                  <li v-for="wp in bp.work_products" :key="wp" class="flex items-start gap-2">
                    <i class="pi pi-file text-xs mt-1" style="color: var(--text-muted);"></i>
                    <span>{{ workProductLabel(wp) }}</span>
                  </li>
                </ul>
              </div>
              <div v-if="bp.tooling_references?.length">
                <h4 class="text-xs font-semibold uppercase tracking-wide mb-2" style="color: var(--text-tertiary);">Tooling</h4>
                <div class="flex flex-wrap gap-1.5">
                  <span v-for="t in bp.tooling_references" :key="t"
                        class="px-2 py-0.5 rounded text-xs"
                        style="background: var(--background-secondary); color: var(--text-secondary);"
                        :title="t">
                    {{ toolLabel(t) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </AccordionTab>
      </Accordion>

      <div v-if="processOutputs.length" class="mt-6">
        <h4 class="text-xs font-semibold uppercase tracking-wide mb-2" style="color: var(--text-tertiary);">Process outputs</h4>
        <div class="grid gap-2 md:grid-cols-2">
          <div v-for="out in processOutputs" :key="out.name"
               class="p-3 rounded" style="background: var(--background-secondary);">
            <div class="text-sm font-medium" style="color: var(--text-primary);">{{ out.name }}</div>
            <div v-if="out.composed_from?.length" class="text-xs mt-1" style="color: var(--text-tertiary);">
              composed from {{ out.composed_from.length }} work product{{ out.composed_from.length > 1 ? 's' : '' }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="text-center py-12">
      <i class="pi pi-sitemap text-4xl mb-4" style="color: var(--text-muted);"></i>
      <p style="color: var(--text-secondary);">No base practices available for this section.</p>
      <p class="text-sm mt-2" style="color: var(--text-muted);">
        Base practices are specific to process domains.
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';
import type { Section } from '@/types/ashmatics';
import { useCharModel } from '@/composables/useCharModel';
import { useExplorerStore } from '@/stores/explorerStore';

const props = defineProps<{
  section: Section | null;
}>();

const { processDomainFiles, toolLabel } = useCharModel();
const explorerStore = useExplorerStore();

// For a process item the section id is its domain code (RM, SA, …).
const domain = computed(() => {
  const code = props.section?.domain_code || props.section?.id;
  return code ? processDomainFiles[code] : undefined;
});

const basePractices = computed(() =>
  [...(domain.value?.base_practices ?? [])].sort((a, b) => a.order - b.order)
);

const processOutputs = computed(() => domain.value?.process_outputs ?? []);

const sopsFor = (practiceId: string) =>
  (domain.value?.standard_operating_procedures ?? []).filter((s) => s.linked_practices.includes(practiceId));

const openSop = (slug: string) => explorerStore.openSopArtifact(slug);

const humanize = (s: string) => s.replace(/_/g, ' ');

/** 'WP-RM-01-Hazard-Analysis-Report.md' -> 'Hazard Analysis Report' */
const workProductLabel = (wp: string) =>
  wp.replace(/\.md$/, '').replace(/^WP-[A-Z]+-\w+?-/, '').replace(/-/g, ' ');
</script>
